
import React, { useRef, useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { GenerationTask, TaskStatus } from '../types';
import { deleteTask } from '../services/storageService';
import { localDbService } from '../services/localDbService';
import { splitLongParagraphs } from '../lib/splitParagraphs';
import { APP_VERSION, BUILD_TIME } from '../constants';

interface GeneratorProps {
  tasks: GenerationTask[];
  agents: { id: string; name: string }[];
  onEditTask: (taskId: string) => void;
  onNewTask?: (task: GenerationTask) => void;
  selectedAgentName: string | 'all';
  onSelectAgent: (agentName: string | 'all') => void;
  onDeleteTask?: (taskId: string) => Promise<void>;
  onBulkDelete?: (taskIds: string[]) => Promise<void>;
  error?: string | null;
  isSidebarOpen?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  totalCount?: number;
  onOpenChannelEditor?: () => void;
  categories: string[];
  onAddCategory: () => void;
  onRemoveCategory: (cat: string) => void;
  newCategoryName: string;
  setNewCategoryName: (val: string) => void;
  showAddCategory: boolean;
  setShowAddCategory: (val: boolean) => void;
  isAdmin?: boolean;
}

const applyUserCleaningRules = (content: string) => {
  let cleaned = content;
  
  cleaned = cleaned.replace(/(!\[(.*?)\]\(.*?\))[ \t]*\r?\n[ \t]*([^\r\n]+)/g, (match, p1, p2, p3) => {
    const altText = p2.trim();
    const nextLine = p3.trim();
    if (altText && nextLine && altText === nextLine) {
      return p1;
    }
    return match;
  });

  cleaned = splitLongParagraphs(cleaned);

  return cleaned;
};

export const Generator: React.FC<GeneratorProps> = ({ 
  tasks, 
  agents, 
  onEditTask,
  onNewTask,
  selectedAgentName,
  onSelectAgent,
  onDeleteTask,
  onBulkDelete,
  error,
  isSidebarOpen = true,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  totalCount = 0,
  onOpenChannelEditor,
  categories,
  onAddCategory,
  onRemoveCategory,
  newCategoryName,
  setNewCategoryName,
  showAddCategory,
  setShowAddCategory,
  isAdmin = false
}) => {
  const markdownImportRef = useRef<HTMLInputElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [sortByRegistration, setSortByRegistration] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [displayLimit, setDisplayLimit] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    message: '',
    onConfirm: () => {}
  });

  const showConfirm = (message: string, onConfirm: () => void) => {
    setConfirmModal({
      isOpen: true,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const filteredTasks = useMemo(() => {
    let result = [...tasks];
    if (selectedAgentName !== 'all') {
      result = result.filter(t => t.agentName === selectedAgentName);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter(t =>
        (t.presetName || '').toLowerCase().includes(query) ||
        (t.articleTitle || '').toLowerCase().includes(query) ||
        (t.inputSummary || '').toLowerCase().includes(query)
      );
    }

    if (sortByRegistration) {
      result.sort((a, b) => {
        const regA = a.isRegistered ? 1 : 0;
        const regB = b.isRegistered ? 1 : 0;
        if (regA !== regB) return regB - regA; // Registered (1) before Unregistered (0)
        return b.createdAt - a.createdAt;
      });
    } else {
      result.sort((a, b) => b.createdAt - a.createdAt);
    }

    return result;
  }, [tasks, selectedAgentName, sortByRegistration, searchQuery]);

  const paginatedTasks = useMemo(() => {
    return filteredTasks.slice(0, displayLimit);
  }, [filteredTasks, displayLimit]);

  const hasMoreLocal = displayLimit < filteredTasks.length;

  const handleLoadMoreLocal = () => {
    setDisplayLimit(prev => prev + 20);
  };

  // Reset display limit when agent or search query changes
  React.useEffect(() => {
    setDisplayLimit(20);
  }, [selectedAgentName, searchQuery]);

  const handleImportMarkdown = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileList = Array.from(files);
    const now = Date.now();
    
    try {
      const results = await Promise.all(fileList.map(async (file, i) => {
        const content = await file.text();
        if (!content) return null;
        
        const cleanedContent = applyUserCleaningRules(content);
        let title = file.name;
        if (title.length > 50) {
          title = title.slice(0, 50);
        }

        const newTask: GenerationTask = {
          id: `${now}-${i}-${Math.random().toString(36).substr(2, 5)}`,
          presetName: `Imported: ${title}`,
          status: TaskStatus.COMPLETED,
          inputSummary: `File: ${file.name}`,
          outputContent: cleanedContent,
          createdAt: now - i,
          agentName: selectedAgentName === 'all' ? undefined : selectedAgentName
        };
        await localDbService.saveDraft(newTask.id, newTask.outputContent, '', newTask.presetName, { agentName: newTask.agentName });
        onNewTask?.(newTask);
        return newTask;
      }));

      const newTasks = results.filter((t): t is GenerationTask => t !== null);
      if (newTasks.length > 0) {
        alert(`${newTasks.length}개의 문서를 성공적으로 불러왔습니다.`);
      }
    } catch (err) {
      console.error("Failed to import files", err);
      alert("파일을 불러오는 중 오류가 발생했습니다.");
    }
    
    e.target.value = '';
  };

  const handleNewMarkdown = async () => {
    const taskId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    let title = `새 문서`;
    if (title.length > 50) {
      title = title.slice(0, 50);
    }
    const newTask: GenerationTask = {
      id: taskId,
      presetName: title,
      status: TaskStatus.COMPLETED,
      inputSummary: `직접 생성`,
      outputContent: '',
      createdAt: Date.now(),
      agentName: selectedAgentName === 'all' ? undefined : selectedAgentName
    };
    try {
      await localDbService.saveDraft(newTask.id, newTask.outputContent, '', newTask.presetName, { 
        agentName: newTask.agentName,
        createdAt: newTask.createdAt 
      });
      onNewTask?.(newTask);
      onEditTask(taskId);
    } catch (e) {
      console.error("Failed to create new document", e);
    }
  };

  const handleSaveMarkdown = (task: GenerationTask) => {
    const titleHeader = task.articleTitle ? `# ${task.articleTitle}\n\n` : '';
    const blob = new Blob([titleHeader + task.outputContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    
    const cleanName = task.presetName.replace(/[^a-z0-9ㄱ-ㅎㅏ-ㅣ가-힣]/gi, '_').toLowerCase();
    const dateStr = new Date(task.createdAt).toISOString().slice(0, 10);
    
    a.href = url;
    a.download = `${cleanName}_${dateStr}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDeleteTask = (e: React.MouseEvent, taskId: string) => {
      e.preventDefault();
      e.stopPropagation();
      showConfirm('이 문서를 삭제하시겠습니까?', async () => {
          try {
            if (onDeleteTask) {
              await onDeleteTask(taskId);
            } else {
              await deleteTask(taskId);
            }
          } catch (e) {
            console.error("Failed to delete task", e);
          }
      });
  };

  const handleClearHistory = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      showConfirm('현재 목록의 모든 문서를 삭제하시겠습니까?', async () => {
          try {
            if (onBulkDelete) {
              await onBulkDelete(filteredTasks.map(t => t.id));
            } else {
              await Promise.all(filteredTasks.map(t => deleteTask(t.id)));
            }
          } catch (e) {
            console.error("Failed to clear history", e);
          }
      });
  };

  const toggleTaskSelection = (taskId: string, index: number, isShiftKey: boolean) => {
    if (isShiftKey && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const newSelected = new Set(selectedTaskIds);
      for (let i = start; i <= end; i++) {
        newSelected.add(filteredTasks[i].id);
      }
      setSelectedTaskIds(newSelected);
    } else {
      setSelectedTaskIds(prev => {
        const next = new Set(prev);
        if (next.has(taskId)) {
          next.delete(taskId);
        } else {
          next.add(taskId);
        }
        return next;
      });
    }
    setLastSelectedIndex(index);
  };

  const handleBulkDelete = () => {
    if (selectedTaskIds.size === 0) return;
    showConfirm(`${selectedTaskIds.size}개의 문서를 삭제하시겠습니까?`, async () => {
      try {
        if (onBulkDelete) {
          await onBulkDelete(Array.from(selectedTaskIds));
        } else {
          await Promise.all(Array.from(selectedTaskIds).map(id => deleteTask(id)));
        }
        setSelectedTaskIds(new Set());
        setIsSelectionMode(false);
      } catch (e) {
        console.error("Failed to delete selected tasks", e);
      }
    });
  };

  // Infinite scroll observer
  React.useEffect(() => {
    if (!hasMoreLocal || !handleLoadMoreLocal) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMoreLocal();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMoreLocal]);

  return (
    <div className="flex h-full overflow-hidden bg-slate-50">
      {/* Sidebar for Agents */}
      {isSidebarOpen && (
        <div className="fixed inset-y-0 left-0 z-[110] w-72 md:relative md:inset-auto md:z-auto bg-white border-r border-slate-200 flex flex-col flex-shrink-0 animate-in slide-in-from-left duration-300">
          <div className="p-6 border-b border-slate-200 flex justify-between items-center">
            <h2 className="text-lg font-bold text-slate-900">에이전트</h2>
          </div>
          
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {isAdmin && (
              <button
                onClick={() => onSelectAgent('all')}
                className={`w-full text-left px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-3 ${
                  selectedAgentName === 'all' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                전체 에이전트
              </button>
            )}
            
            {agents.map(agent => (
              <div
                key={agent.id}
                onClick={() => onSelectAgent(agent.name)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onSelectAgent(agent.name)}
                className={`w-full text-left px-4 py-2 rounded-lg text-sm font-bold transition-colors group flex items-center justify-between cursor-pointer ${
                  selectedAgentName === agent.name ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center gap-3 truncate">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <span className="truncate">{agent.name}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-slate-200">
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest">카테고리 관리</h3>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {categories.map(cat => (
                  <div 
                    key={cat}
                    className="group flex items-center gap-1 px-2 py-1 bg-slate-100 border border-slate-200 rounded-md text-xs font-bold text-slate-600 transition-all"
                  >
                    <span>{cat}</span>
                    <button 
                      onClick={() => onRemoveCategory(cat)}
                      className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                <button 
                  onClick={() => setShowAddCategory(true)}
                  className="px-2 py-1 rounded-md text-xs font-bold border border-dashed border-slate-300 text-slate-400 hover:border-blue-500 hover:text-blue-500 transition-all"
                >
                  + 추가
                </button>
              </div>

              <AnimatePresence>
                {showAddCategory && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-center gap-2 overflow-hidden mb-4"
                  >
                    <input 
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="이름..."
                      className="flex-1 px-2 py-1.5 bg-white border border-slate-200 rounded text-xs outline-none focus:border-blue-500"
                      onKeyDown={(e) => e.key === 'Enter' && onAddCategory()}
                    />
                    <button 
                      onClick={onAddCategory}
                      className="px-2 py-1.5 bg-blue-600 text-white text-xs font-bold rounded hover:bg-blue-700 transition-colors"
                    >
                      추가
                    </button>
                    <button 
                      onClick={() => setShowAddCategory(false)}
                      className="p-1 text-slate-400 hover:text-slate-600"
                    >
                      <X size={16} />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {isAdmin && (
              <button
                onClick={onOpenChannelEditor}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all border border-slate-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37a1.724 1.724 0 002.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                채널 및 권한 관리
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col h-full overflow-hidden relative">
        <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
            <div className="max-w-5xl mx-auto px-4 md:px-6 py-4 md:py-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex flex-wrap items-center gap-2 md:gap-4">
                  <h1 className="text-2xl font-bold text-slate-900">
                    {selectedAgentName === 'all' ? '전체 에이전트' : selectedAgentName}
                  </h1>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="문서 검색..."
                      className="pl-9 pr-8 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 outline-none focus:bg-white focus:border-blue-500 transition-colors w-48 md:w-64"
                    />
                    <svg className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <button
                      onClick={() => setSortByRegistration(!sortByRegistration)}
                      className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                        sortByRegistration ? 'bg-blue-600 text-white border border-blue-700' : 'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}
                    >
                      등록 우선 정렬 {sortByRegistration ? 'ON' : 'OFF'}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 md:gap-3">
                    {isSelectionMode ? (
                      <>
                        <button 
                            type="button" 
                            onClick={handleBulkDelete} 
                            disabled={selectedTaskIds.size === 0}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white transition-colors shadow-sm ${
                              selectedTaskIds.size > 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'
                            }`}
                        >
                            삭제 ({selectedTaskIds.size})
                        </button>
                        <button 
                            type="button" 
                            onClick={() => {
                              setIsSelectionMode(false);
                              setSelectedTaskIds(new Set());
                            }} 
                            className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            취소
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                            type="button" 
                            onClick={() => setIsSelectionMode(true)} 
                            className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-bold text-slate-700 transition-colors border border-slate-200"
                        >
                            선택 삭제
                        </button>
                        <button 
                            type="button" 
                            onClick={handleNewMarkdown} 
                            className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-bold text-white transition-colors shadow-sm"
                        >
                            새 마크다운
                        </button>
                        <button 
                            type="button" 
                            onClick={() => markdownImportRef.current?.click()} 
                            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-bold text-white transition-colors shadow-sm"
                        >
                            마크다운 불러오기
                            <input type="file" accept=".md,.txt" multiple ref={markdownImportRef} className="hidden" onChange={handleImportMarkdown} />
                        </button>
                        <a 
                            href="https://www.notion.so/flowing/337bb2c4092280ec999bee37a22a5ee8?v=337bb2c40922800292b2000c7f3b5677" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 rounded-lg text-sm font-bold text-white transition-colors shadow-sm"
                        >
                            노션 확인
                        </a>
                        {filteredTasks.length > 0 && (
                            <button 
                                type="button" 
                                onClick={handleClearHistory} 
                                className="px-3 py-2 text-sm font-medium text-slate-500 hover:text-red-600 transition-colors"
                            >
                                목록 삭제
                            </button>
                        )}
                      </>
                    )}
                </div>
            </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-5xl mx-auto">
                {filteredTasks.length === 0 && (
                    <div className="text-center py-20 border-2 border-dashed border-slate-200 rounded-2xl bg-white flex flex-col items-center px-10">
                        {error ? (
                          <>
                            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4 text-red-500">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                            </div>
                            <p className="text-lg font-bold text-slate-900">
                              {error.includes('permission') || error.includes('권한') 
                                ? "접근 권한이 없는 문서가 포함되어 있습니다." 
                                : "구글 할당량 차단으로 문서가 보이지 않습니다."}
                            </p>
                            <p className="text-sm text-slate-500 mt-2 mb-6 max-w-md">
                              {error.includes('permission') || error.includes('권한')
                                ? "현재 계정에 허용된 에이전트의 문서만 조회할 수 있습니다. 권한이 올바른지 확인해 주세요."
                                : "유료 플랜임에도 구글의 시스템 한도 정책으로 인해 자동 복구가 차단되었습니다."}
                              <span className="block mt-2 font-bold text-red-600">지금 당장 편집이 필요하시다면 아래 '수동 복사' 방법을 이용해 주세요.</span>
                            </p>
                            
                            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 text-left w-full max-w-lg mb-8">
                              <h5 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                                <span className="w-5 h-5 bg-slate-800 text-white rounded-full flex items-center justify-center text-[10px]">1</span>
                                수동 복사 및 복구 방법
                              </h5>
                              <ol className="text-xs text-slate-600 space-y-2 list-decimal pl-4">
                                <li><a href="https://console.firebase.google.com/project/cartech-461502/firestore/databases/ai-studio-684a421c-6666-4cc2-b83a-99dbc260fbf6/data/tasks" target="_blank" rel="noreferrer" className="text-blue-600 font-bold underline">여기(파이어베이스 콘솔)</a>에 접속합니다.</li>
                                <li>목록에서 문서를 선택하고 <b>outputContent</b> 필드의 내용을 복사합니다.</li>
                                <li>이 앱으로 돌아와 <b>[새 마크다운]</b>을 누르고 내용을 붙여넣습니다.</li>
                              </ol>
                            </div>

                            <div className="flex gap-3">
                              <button 
                                onClick={() => window.location.reload()}
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-200"
                              >
                                새로고침
                              </button>
                            </div>
                          </>
                        ) : selectedAgentName !== 'all' ? (
                          <>
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-400">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                              </svg>
                            </div>
                            <p className="text-lg font-bold text-slate-900">이 에이전트에 문서가 없습니다.</p>
                            <p className="text-sm text-slate-500 mt-2 mb-6">새 문서를 작성하거나 다른 에이전트를 선택해 보세요.</p>
                            <button 
                                onClick={handleNewMarkdown} 
                                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-green-200"
                            >
                                새 마크다운 작성하기
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-400">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <p className="text-lg font-bold text-slate-900">작성된 문서가 없습니다.</p>
                            <p className="text-sm text-slate-500 mt-2 mb-6">첫 번째 문서를 작성해 보세요!</p>
                            <button 
                                onClick={handleNewMarkdown} 
                                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-green-200"
                            >
                                새 마크다운 작성하기
                            </button>
                          </>
                        )}
                    </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {paginatedTasks.map((task, index) => {
                        const isModified = !!task.hasLocalEdits;
                        return (
                        <div 
                          key={task.id} 
                          onClick={(e) => isSelectionMode && toggleTaskSelection(task.id, index, e.shiftKey)}
                          className={`bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col relative ${
                            isSelectionMode ? 'cursor-pointer select-none' : ''
                          } ${
                            selectedTaskIds.has(task.id) ? 'border-red-500 ring-2 ring-red-100' : 
                            task.isRegistered ? 'bg-sky-50 border-sky-200 ring-1 ring-sky-100' : 'border-slate-200'
                          }`}
                        >
                            {isSelectionMode && (
                              <div className="absolute top-4 left-4 z-10">
                                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                  selectedTaskIds.has(task.id) ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-slate-300'
                                }`}>
                                  {selectedTaskIds.has(task.id) && (
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="p-5 flex-1 relative">
                                {task.isRegistered && (
                                  <span className="absolute top-4 right-4 px-2 py-1 bg-blue-600 text-white text-[10px] font-bold rounded shadow-sm flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    등록 완료
                                  </span>
                                )}
                                <h4 className={`font-bold mb-1 break-words pr-16 ${isSelectionMode ? 'pl-8' : ''} ${isModified ? 'text-orange-500' : 'text-slate-900'}`}>{task.presetName}</h4>
                                <p className={`text-xs text-slate-500 ${isSelectionMode ? 'pl-8' : ''}`}>{new Date(task.createdAt).toLocaleString()}</p>
                                <p className={`text-[10px] text-slate-400 mt-1 ${isSelectionMode ? 'pl-8' : ''}`}>Agent: {task.agentName || 'None'}</p>
                                <p className={`text-xs text-slate-400 mt-2 truncate ${isSelectionMode ? 'pl-8' : ''}`}>{task.inputSummary}</p>
                            </div>
                            <div className="bg-slate-50 px-4 py-3 flex justify-between items-center border-t border-slate-200">
                                <div className="flex gap-2">
                                    <button 
                                      onClick={(e) => {
                                        if (isSelectionMode) {
                                          e.stopPropagation();
                                          toggleTaskSelection(task.id, index, e.shiftKey);
                                        } else {
                                          onEditTask(task.id);
                                        }
                                      }} 
                                      className="px-3 py-1.5 bg-slate-900 text-white text-xs font-bold rounded-md hover:bg-slate-800 transition-colors"
                                    >
                                        {isSelectionMode ? (selectedTaskIds.has(task.id) ? '해제' : '선택') : '편집'}
                                    </button>
                                    {!isSelectionMode && (
                                      <button 
                                          onClick={() => handleSaveMarkdown(task)} 
                                          className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-md hover:bg-slate-50 transition-colors"
                                      >
                                          저장
                                      </button>
                                    )}
                                </div>
                                {!isSelectionMode && (
                                  <button type="button" onClick={(e) => handleDeleteTask(e, task.id)} className="text-xs font-bold text-slate-400 hover:text-red-600 transition-colors">
                                      삭제
                                  </button>
                                )}
                            </div>
                        </div>
                    )})}
                </div>

                {/* Load More Trigger */}
                {hasMoreLocal && (
                  <div ref={loadMoreRef} className="py-10 flex justify-center">
                    <button 
                      onClick={handleLoadMoreLocal}
                      className="px-6 py-2 bg-white border border-slate-200 rounded-full text-sm font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      더 보기
                    </button>
                  </div>
                )}
            </div>
        </div>
      </div>

      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-slate-900 mb-2">확인</h3>
            <p className="text-slate-600 mb-6">{confirmModal.message}</p>
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
              >
                취소
              </button>
              <button 
                onClick={confirmModal.onConfirm}
                className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
